import { DerbyRngV1 } from '../rng';
import {
  DRILL_TICK_S,
  steerOf,
  type MinigameDef,
  type MinigameSession,
  type SessionScore,
} from './framework';

/**
 * Heart @ the Round Pen — composure. Spooks (tarps, geese, umbrellas)
 * appear to one side and the horse drifts toward whatever it stares at;
 * steer it back to center. Getting too close drains Calm; Calm at zero
 * and the horse bolts. Holding center through a spook is a clean pass.
 */

export type SpookKind = 'tarp' | 'goose' | 'umbrella' | 'bucket';

export interface PenSpook {
  kind: SpookKind;
  side: -1 | 1;
  startTick: number;
  endTick: number;
  breached: boolean;
}

export interface PenTierConfig {
  durationS: number;
  corridor: number;
  pull: number;
  paired: boolean;
  spookEveryS: number;
}

export function penTier(tier: number): PenTierConfig {
  switch (Math.max(1, Math.min(4, tier))) {
    case 1:
      return { durationS: 15, corridor: 0.35, pull: 0.5, paired: false, spookEveryS: 2.4 };
    case 2:
      return { durationS: 15, corridor: 0.3, pull: 0.7, paired: false, spookEveryS: 2.2 };
    case 3:
      return { durationS: 15, corridor: 0.28, pull: 0.8, paired: true, spookEveryS: 2 };
    default:
      return { durationS: 15, corridor: 0.25, pull: 0.9, paired: true, spookEveryS: 1.8 };
  }
}

const SPOOK_KINDS: SpookKind[] = ['tarp', 'goose', 'umbrella', 'bucket'];
const STEER_RATE = 1.3;
const CENTERING_RATE = 0.3;
const BREACH_AT = 0.55;
const CALM_REGEN = 0.06;
const BOND_STREAK = 3;

export type PenEvent =
  | { kind: 'spook'; spook: SpookKind; side: -1 | 1 }
  | { kind: 'clean'; streak: number }
  | { kind: 'breached' }
  | { kind: 'bolt' }
  | { kind: 'done' };

export class PenSession implements MinigameSession {
  readonly config: PenTierConfig;
  readonly spooks: PenSpook[];
  readonly durationTicks: number;
  readonly drainMul: number;

  tick = 0;
  /** Lateral position, -1..1; 0 is centered. */
  p = 0;
  calm = 1;
  bolted = false;
  cleanPasses = 0;
  streak = 0;
  private maxStreak = 0;
  private centeredTicks = 0;
  private done = false;
  private readonly events: PenEvent[] = [];
  private announced = new Set<number>();

  constructor(tier: number, seed: number, mods?: Record<string, number>) {
    this.drainMul = mods?.penDrainMul ?? 1;
    this.config = penTier(tier);
    this.durationTicks = Math.round(this.config.durationS / DRILL_TICK_S);
    const rng = new DerbyRngV1(seed).fork('pen');
    const spooks: PenSpook[] = [];
    let at = Math.round(1 / DRILL_TICK_S);
    while (at < this.durationTicks - 30) {
      const side = rng.chance(0.5) ? 1 : -1;
      const duration = Math.round(rng.range(1.5, 2.5) / DRILL_TICK_S);
      spooks.push({
        kind: rng.pick(SPOOK_KINDS),
        side: side as -1 | 1,
        startTick: at,
        endTick: at + duration,
        breached: false,
      });
      if (this.config.paired && rng.chance(0.4)) {
        const pairDuration = Math.round(rng.range(1.2, 1.8) / DRILL_TICK_S);
        spooks.push({
          kind: rng.pick(SPOOK_KINDS),
          side: -side as -1 | 1,
          startTick: at + Math.round(duration * 0.7),
          endTick: at + Math.round(duration * 0.7) + pairDuration,
          breached: false,
        });
      }
      at += Math.round(rng.range(this.config.spookEveryS * 0.8, this.config.spookEveryS * 1.3) / DRILL_TICK_S);
    }
    this.spooks = spooks;
  }

  get finished(): boolean {
    return this.done;
  }

  activeSpooks(): PenSpook[] {
    return this.spooks.filter((s) => s.startTick <= this.tick && this.tick < s.endTick);
  }

  drainEvents(): PenEvent[] {
    return this.events.splice(0, this.events.length);
  }

  step(mask: number): void {
    if (this.done) return;
    const dt = DRILL_TICK_S;
    const steer = steerOf(mask);
    this.p += steer * STEER_RATE * dt;

    const active = this.activeSpooks();
    for (const spook of active) {
      const index = this.spooks.indexOf(spook);
      if (!this.announced.has(index)) {
        this.announced.add(index);
        this.events.push({ kind: 'spook', spook: spook.kind, side: spook.side });
      }
      this.p += spook.side * this.config.pull * dt;
      // Straying toward the spook drains Calm, faster the closer you get.
      const closeness = Math.max(0, this.p * spook.side - 0.4);
      if (closeness > 0) this.calm -= closeness * 1.4 * this.drainMul * dt;
      if (this.p * spook.side >= BREACH_AT && !spook.breached) {
        spook.breached = true;
        this.events.push({ kind: 'breached' });
      }
    }
    if (active.length === 0) {
      this.p -= Math.sign(this.p) * Math.min(Math.abs(this.p), CENTERING_RATE * dt);
      this.calm = Math.min(1, this.calm + CALM_REGEN * dt);
    }
    this.p = Math.max(-1, Math.min(1, this.p));

    if (Math.abs(this.p) < this.config.corridor) this.centeredTicks += 1;

    if (this.calm <= 0) {
      this.calm = 0;
      this.bolted = true;
      this.done = true;
      this.events.push({ kind: 'bolt' });
      return;
    }

    // A spook that just ended without a breach is a clean pass.
    for (const spook of this.spooks) {
      if (spook.endTick === this.tick) {
        if (spook.breached) {
          this.streak = 0;
        } else {
          this.cleanPasses += 1;
          this.streak += 1;
          this.maxStreak = Math.max(this.maxStreak, this.streak);
          this.events.push({ kind: 'clean', streak: this.streak });
          if (this.streak >= BOND_STREAK) this.calm = Math.min(1, this.calm + 0.08);
        }
      }
    }

    this.tick += 1;
    if (this.tick >= this.durationTicks) {
      this.done = true;
      this.events.push({ kind: 'done' });
    }
  }

  score(): SessionScore {
    const centeredFraction = Math.min(1, this.centeredTicks / this.durationTicks);
    const cleanFraction = this.spooks.length > 0 ? this.cleanPasses / this.spooks.length : 1;
    const raw = Math.max(0, Math.min(1, 0.7 * centeredFraction + 0.3 * cleanFraction));
    return {
      raw,
      detail: `${this.cleanPasses}/${this.spooks.length} clean passes · bond ×${this.maxStreak}${this.bolted ? ' · bolted!' : ''}`,
      failed: this.bolted,
    };
  }
}

export const PEN_DEF: MinigameDef = {
  id: 'pen',
  name: 'Round Pen',
  stat: 'heart',
  energyCost: 4,
  create: (tier, seed, mods) => new PenSession(tier, seed, mods),
};
